"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import useIsMobile from "../../common/utils/useIsMobile";
import { usePathname } from "../../../navigation";

const NavigationWrapper = ({ children, navigationData }) => {
  const [selectedLayout, setSelectedLayout] = useState(null);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const pathname = usePathname();
  const menuRef = useRef(null);

  // Define isMobile first without callback
  const isMobile = useIsMobile();

  const resetMenuState = useCallback(() => {
    // On mobile, only reset the selected layout, not the menu open state
    if (isMobile) {
      setSelectedLayout(null);
    } else {
      // On desktop, reset both
      setMobileMenuOpen(false);
      setSelectedLayout(null);
    }
  }, [isMobile]);

  const handleTopLevelClick = (item) => {
    setSelectedLayout(
      item?.meta?.layout === selectedLayout
        ? null
        : item?.meta?.layout || "services",
    );
  };
  const backToMainMenu = () => {
    setSelectedLayout(null);
  };
  useEffect(() => {
    // Detect if device has touch capability
    const isTouchDevice =
      "ontouchstart" in window || navigator.maxTouchPoints > 0;

    // Only add auto-close behavior for large desktop screens without touch
    if (window.innerWidth <= 992 || isTouchDevice) {
      return; // No auto-close on mobile/tablet/touch screens
    }

    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        !event.target.closest("#navigation-search-input")
      ) {
        resetMenuState();
      }
    };

    const handleMouseOut = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.relatedTarget) &&
        event.relatedTarget &&
        !event.relatedTarget.closest(".mkt-nav") &&
        !event.relatedTarget.closest("#navigation-search-input")
      ) {
        resetMenuState();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    if (menuRef.current) {
      menuRef.current.addEventListener("mouseleave", handleMouseOut);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (menuRef.current) {
        menuRef.current.removeEventListener("mouseleave", handleMouseOut);
      }
    };
  }, [resetMenuState]);
  const currentNavigation = navigationData.find(
    ({ meta }) =>
      meta?.layout === selectedLayout ||
      (selectedLayout === "services" && !meta?.layout),
  );

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen((prev) => !prev);
    setSelectedLayout(null);
  }, []);

  // Close mobile menu when a navigation item is clicked
  const handleNavigationClick = useCallback(() => {
    if (isMobile) {
      setMobileMenuOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    // On route change, close the mobile menu
    if (isMobile) {
      setMobileMenuOpen(false);
    } else {
      resetMenuState();
    }
  }, [pathname, isMobile, resetMenuState]);

  return children({
    isMobile,
    selectedLayout,
    setSelectedLayout,
    handleTopLevelClick,
    toggleMobileMenu,
    currentNavigation,
    menuRef,
    resetMenuState,
    isMobileMenuOpen,
    backToMainMenu,
    handleNavigationClick,
  });
};

export default NavigationWrapper;
